/**
 * YourTasksTodayCard — compact summary card on the home screen.
 *
 * Default state is a tight one-row card:
 *   "Your Tasks Today · 1 of 3 completed · 2 need acknowledgment · [View Tasks]"
 * Tapping "View Tasks" opens a bottom-sheet overlay containing the full
 * accountability flow (Acknowledge → Mark In Progress → Mark Complete,
 * with manager note rendering when status = needs_attention).
 *
 * Each task is labeled by category (Zone - ACOs · Win the Day - Emails ·
 * Task - T-Shirt Wall) instead of the generic "Coverage" so crew can
 * tell at a glance where the assignment came from. Categorization rules
 * live in `lib/coverageTasks.js`.
 *
 * Hidden when the user is not on shift AND has no tasks.
 *
 * User ask 2026-02-15: "keep the home screen clean while still making
 * assignments visible and accountable".
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardText, CheckCircle, Eye, Warning, ShieldCheck,
  ArrowRight, PlayCircle, Camera, HandWaving, X, Plus, Minus, Package, Check, CircleNotch, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { categorizeTask, categorizeTaskLabel, STATUS_META, taskAttributionLine } from "../lib/coverageTasks";
import { compressImageToDataUrl } from "../lib/imageCompress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayYmd = () => {
  const now = new Date();
  const mst = new Date(now.toLocaleString("en-US", { timeZone: "America/Denver" }));
  return `${mst.getFullYear()}-${String(mst.getMonth() + 1).padStart(2, "0")}-${String(mst.getDate()).padStart(2, "0")}`;
};

export default function YourTasksTodayCard({
  crewId,
  storeId,
  onOpen,
  // NEW (2026-XX): controlled-Sheet + headless mode props.
  //  - `hideSummary`: when true, the visible yellow summary bar is not
  //    rendered — only the internal Sheet mounts. Lets HomePage.js drop
  //    a "hidden" instance in the tree and open the task Sheet from the
  //    new top-of-home Tasks summary card without duplicating the bar.
  //  - `open` / `onOpenChange`: when both provided, the Sheet is
  //    externally controlled (mirrors the Radix `Dialog`/`Sheet` API).
  //    Falling back to internal state when either is undefined
  //    preserves every existing caller's behaviour.
  hideSummary = false,
  open,
  onOpenChange,
}) {
  const [data, setData] = useState(null);
  const [sheetOpenLocal, setSheetOpenLocal] = useState(false);
  // Freight self-report card (2026-09-02) — surfaces this crew's active
  // freight_assignments row inline in the task list so they can log
  // pallet progress from the same menu they already open for tasks.
  const [freight, setFreight] = useState({ assignment: null, backroom_now: 0 });
  const [freightSheetOpen, setFreightSheetOpen] = useState(false);
  const [freightCount, setFreightCount] = useState(1);
  const [freightNote, setFreightNote] = useState("");
  const [freightSubmitting, setFreightSubmitting] = useState(false);
  const isControlled = typeof open === "boolean" && typeof onOpenChange === "function";
  const sheetOpen = isControlled ? open : sheetOpenLocal;
  const setSheetOpen = isControlled ? onOpenChange : setSheetOpenLocal;
  const [busyId, setBusyId] = useState("");
  const date = todayYmd();

  const load = useCallback(async () => {
    if (!crewId) return;
    // Skip while tab is hidden — battery/data saver. (2026-08-01)
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const r = await axios.get(`${API}/zone-sheets/my/${date}`, {
        params: { actor_id: crewId, store_id: storeId || "" },
      });
      setData(r.data);
    } catch {
      setData({ tasks: [], on_shift: false });
    }
    // Freight assignment fetch runs alongside tasks — same crew, same
    // date. Silent on 404s so the row simply hides when the crew isn't
    // on freight today.
    try {
      const fr = await axios.get(`${API}/freight/${date}/my-assignment`, {
        params: { actor_id: crewId, store_id: storeId || "" },
      });
      setFreight({
        assignment: fr.data?.assignment || null,
        backroom_now: Number(fr.data?.backroom_now || 0),
      });
    } catch {
      setFreight({ assignment: null, backroom_now: 0 });
    }
  }, [crewId, storeId, date]);

  useEffect(() => {
    load();
    const t = setInterval(load, 45_000);
    // SSE live push — when a manager flips this crew's task to
    // needs_attention or manager_verified, refetch immediately so they
    // see the new state without waiting for the 45s poll.
    const onSSE = (e) => {
      const evt = e.detail || {};
      if (evt.kind !== "coverage_task_update") return;
      if (evt.crew_id && evt.crew_id !== crewId && evt.transition !== "sheet_edit") return;
      load();
    };
    window.addEventListener("wow:notification", onSSE);
    return () => {
      clearInterval(t);
      window.removeEventListener("wow:notification", onSSE);
    };
  }, [load, crewId]);

  const [completePromptTask, setCompletePromptTask] = useState(null); // open photo dialog for this task
  const [handoffPromptTask, setHandoffPromptTask] = useState(null);    // open handoff dialog
  const [handoffNote, setHandoffNote] = useState("");
  // Batch C — peer "Ask for Help" flow (distinct from manager Handoff).
  // Opens a small dialog so the crew can drop an optional note before
  // bell-firing on-shift teammates. (2026-06-15.)
  const [helpPromptTask, setHelpPromptTask] = useState(null);
  const [helpNote, setHelpNote] = useState("");

  const transition = async (task, transitionName, extra = {}) => {
    // Zone tasks (PURGE / RECOVER / PRICE / ADJUST) route to the Zone
    // Sheet PATCH endpoint instead of the coverage-task transition
    // endpoint — different schema. User ask 2026-02: crew wanted a
    // one-tap acknowledge that immediately shows "In Progress" on
    // these 4 world rows without leaving to Zone Sheet.
    if (task.kind === "zone") {
      // Two transitions we support in-sheet:
      //   "in_progress"  → flips in_progress=true (also acts as ack)
      //   "complete"     → flips completed=true (crew self-complete)
      // Anything else falls back to the old "open Zone Sheet" behavior.
      const zoneUpdate =
        transitionName === "in_progress" ? { id: task.task_id, in_progress: true } :
        transitionName === "complete" ? { id: task.task_id, completed: true } :
        null;
      if (!zoneUpdate) {
        setSheetOpen(false);
        onOpen && onOpen();
        return;
      }
      setBusyId(task.task_id);
      try {
        await axios.patch(
          `${API}/zone-sheets/${date}`,
          { actor_id: crewId, zone_task_updates: [zoneUpdate] },
          { params: { store_id: storeId || "" } },
        );
        if (transitionName === "in_progress") {
          toast.success(`${task.label} — started`);
        } else if (transitionName === "complete") {
          toast.success(`${task.label} — marked complete`);
        }
        await load();
        return true;
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(e?.response?.data?.detail || "Couldn't update task");
        return false;
      } finally {
        setBusyId("");
      }
      return;
    }
    if (task.kind === "win_the_day") {
      // WTD delegations route to the zone-sheet PATCH endpoint's
      // `win_the_day_updates` array.
      //   "complete"     → signed_off=true (server flips status to
      //                    pending_verification if photos attached,
      //                    else needs_manager_check).
      //   "upload_before" / "upload_after" → attach photo data URL.
      const beforeUrl = extra.before_photo_url;
      const afterUrl = extra.after_photo_url;
      let update = null;
      if (transitionName === "complete") {
        update = { id: task.task_id, signed_off: true };
        if (beforeUrl) update.before_photo_url = beforeUrl;
        if (afterUrl) update.after_photo_url = afterUrl;
      } else if (transitionName === "upload_before" && beforeUrl) {
        update = { id: task.task_id, before_photo_url: beforeUrl };
      } else if (transitionName === "upload_after" && afterUrl) {
        update = { id: task.task_id, after_photo_url: afterUrl };
      }
      if (!update) return;
      setBusyId(task.task_id);
      try {
        await axios.patch(
          `${API}/zone-sheets/${date}`,
          { actor_id: crewId, win_the_day_updates: [update] },
          { params: { store_id: storeId || "" } },
        );
        if (transitionName === "complete") {
          toast.success(`${task.label} — submitted for verification`);
        } else if (transitionName === "upload_before") {
          toast.success("Before photo attached");
        } else if (transitionName === "upload_after") {
          toast.success("After photo attached");
        }
        await load();
        return true;
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(e?.response?.data?.detail || "Couldn't update task");
        return false;
      } finally {
        setBusyId("");
      }
      return;
    }
    if (task.kind !== "coverage") {
      // Any other non-coverage kind (opening/closing checklist, etc.)
      // still routes to Zone Sheet where they have their own UI.
      setSheetOpen(false);
      onOpen && onOpen();
      return;
    }
    setBusyId(task.task_id);
    try {
      const resp = await axios.post(
        `${API}/zone-sheets/${date}/coverage-tasks/transition`,
        { actor_id: crewId, crew_id: crewId, task_id: task.task_id, transition: transitionName, ...extra },
        { params: { store_id: storeId || "" } },
      );
      // Confirmation toast on terminal-ish transitions so the crew gets
      // immediate feedback that the action stuck. The Sheet stays open
      // on purpose so they can keep reviewing other assignments. (User
      // ask 2026-06-13 "Crew stays in the task overlay/task detail/task
      // list".)
      if (transitionName === "complete") {
        toast.success("Task marked complete — waiting for manager verification");
      } else if (transitionName === "request_handoff") {
        toast.success("Handoff requested — a manager will pick this up");
      } else if (transitionName === "request_help") {
        // Surface zero-recipient case explicitly so the request doesn't
        // feel "stuck". Otherwise the toast claims teammates were
        // pinged even when nobody is on-shift. (Code-review surface
        // 2026-06-15.)
        const count = Number(resp?.data?.help_recipients_count ?? -1);
        if (count === 0) {
          toast.warning("Help requested — but no teammates are on shift right now. Try flagging a manager via Handoff.");
        } else {
          toast.success("Help request sent — a teammate will jump in if they can");
        }
      } else if (transitionName === "cancel_help") {
        toast.success("Help request cancelled");
      } else if (transitionName === "recall") {
        toast.success("Task recalled — finish it whenever you're ready");
      }
      await load();
      return true;
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't update task");
      return false;
    } finally {
      setBusyId("");
    }
  };

  // Hooked from the per-row "Mark Complete" → opens the photo dialog
  // so the crew can attach (or skip) a completion photo before the
  // transition fires.
  const askForPhotoThenComplete = (task) => setCompletePromptTask(task);
  const askForHandoff = (task) => { setHandoffNote(""); setHandoffPromptTask(task); };
  const askForHelp = (task) => { setHelpNote(""); setHelpPromptTask(task); };
  const cancelHelp = (task) => transition(task, "cancel_help");

  // Freight self-report submit (2026-09-02).
  const submitFreight = async () => {
    if (freightSubmitting) return;
    setFreightSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/freight/${date}/log-progress`,
        { actor_id: crewId, pallets: Number(freightCount), note: freightNote || null },
        { params: { store_id: storeId || "" } },
      );
      const logged = Number(r.data?.logged || 0);
      toast.success(`Nice work — logged ${logged} pallet${logged === 1 ? "" : "s"}`);
      setFreight({
        assignment: r.data?.assignment || null,
        backroom_now: Number(r.data?.backroom_now || 0),
      });
      setFreightSheetOpen(false);
      setFreightCount(1);
      setFreightNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log progress");
    } finally {
      setFreightSubmitting(false);
    }
  };

  const summary = useMemo(() => {
    const tasks = data?.tasks || [];
    const done = tasks.filter((t) => t.status === "completed" || t.status === "manager_verified").length;
    const notSeen = tasks.filter((t) => t.status === "not_acknowledged").length;
    const flagged = tasks.filter((t) => t.status === "needs_attention").length;
    const inProgress = tasks.filter((t) => t.status === "in_progress").length;
    const hasFreight = !!freight?.assignment;
    return { total: tasks.length, done, notSeen, flagged, inProgress, hasFreight };
  }, [data, freight]);

  if (!data) return null;
  // Hidden/controlled mount from HomePage still needs to render the
  // Sheet even when the crew is off-shift with no tasks — the outer
  // caller controls when it opens, so bailing here would cause the
  // Tasks summary card to look broken (tap does nothing). The Sheet
  // itself shows the empty-state copy in that case.
  // Also stay mounted when there's an active freight assignment so the
  // crew can log pallets even if they have zero WTD/coverage tasks.
  if (!hideSummary && summary.total === 0 && !data.on_shift && !summary.hasFreight) return null;

  return (
    <>
      {!hideSummary && (
        <CompactCard summary={summary} onView={() => setSheetOpen(true)} />
      )}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl max-h-[88vh] overflow-y-auto pt-3 pb-6 px-4 bg-white"
          data-testid="your-tasks-sheet"
        >
          {/* Bottom-sheet drag handle — visual cue that this can be
              swiped down to dismiss (Radix already handles click-outside
              + Esc; the handle adds an obvious touch target). */}
          <div className="flex justify-center mb-1" aria-hidden="true">
            <div className="w-12 h-1.5 rounded-full bg-slate-300" />
          </div>

          {/* Sticky, hard-to-miss close button at the top right. The
              default shadcn close (h-4 w-4 opacity-70) was too subtle —
              user reported "the X is not obvious". This one is 44px+
              tap target, always visible while scrolling. */}
          <button
            type="button"
            onClick={() => setSheetOpen(false)}
            className="absolute top-3 right-3 w-11 h-11 rounded-full bg-slate-100 hover:bg-slate-200 inline-flex items-center justify-center z-10"
            aria-label="Close"
            data-testid="your-tasks-close"
          >
            <X weight="bold" className="w-5 h-5 text-slate-700" />
          </button>

          <SheetHeader className="text-left mb-3 pr-12">
            <SheetTitle className="text-base font-black">Your Tasks Today</SheetTitle>
            <SheetDescription className="text-xs">
              {summary.total === 0 && !summary.hasFreight
                ? "No specific assignments yet — check back later."
                : `${summary.done} of ${summary.total} completed${summary.notSeen ? ` · ${summary.notSeen} need acknowledgment` : ""}${summary.flagged ? ` · ${summary.flagged} need attention` : ""}${summary.hasFreight ? " · freight assigned" : ""}`}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-2 pb-6">
            {freight.assignment && (
              <FreightAssignmentRow
                assignment={freight.assignment}
                backroomNow={freight.backroom_now}
                onLog={() => {
                  setFreightCount(1);
                  setFreightNote("");
                  setFreightSheetOpen(true);
                }}
              />
            )}
            {(data.tasks || []).map((t) => (
              <TaskRow
                key={`${t.kind}:${t.task_id}`}
                task={t}
                busy={busyId === t.task_id}
                onAck={() => transition(t, "ack")}
                onStart={() => transition(t, "in_progress")}
                onComplete={() => {
                  // Zone tasks skip the photo dialog per the "quick and
                  // easy" ask — go straight to complete. Coverage tasks
                  // still route through the photo prompt. WTD photo-
                  // required tasks handle their own before/after UI in
                  // the row, so onComplete is a direct submit with the
                  // already-attached URLs.
                  if (t.kind === "zone") return transition(t, "complete");
                  if (t.kind === "win_the_day") {
                    return transition(t, "complete", {
                      before_photo_url: t.before_photo_url || "",
                      after_photo_url: t.after_photo_url || "",
                    });
                  }
                  return askForPhotoThenComplete(t);
                }}
                onWtdPhoto={async (which, dataUrl) => {
                  if (!dataUrl) return;
                  await transition(t, which === "before" ? "upload_before" : "upload_after",
                    which === "before" ? { before_photo_url: dataUrl } : { after_photo_url: dataUrl });
                }}
                onRequestHandoff={() => askForHandoff(t)}
                onAskHelp={() => askForHelp(t)}
                onCancelHelp={() => cancelHelp(t)}
                onRecall={() => transition(t, "recall")}
                onOpen={() => { setSheetOpen(false); onOpen && onOpen(); }}
              />
            ))}
            {summary.total === 0 && !freight.assignment && (
              <p className="text-sm text-slate-500 italic text-center py-6">
                You&apos;re on shift today — no specific assignments yet.
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Freight self-report bottom sheet (2026-09-02). Opens when the
          crew taps "Log +" on the FreightAssignmentRow above. */}
      <Sheet open={freightSheetOpen} onOpenChange={setFreightSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl p-0 max-h-[80vh]" data-testid="crew-freight-log-sheet">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <SheetTitle className="text-left flex items-center gap-2">
              <Package weight="fill" className="w-5 h-5 text-orange-600" />
              How many did you knock out?
            </SheetTitle>
          </SheetHeader>
          <div className="px-5 py-6 space-y-5">
            <div className="text-center">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                Backroom right now
              </p>
              <p className="text-3xl font-black text-slate-900" data-testid="crew-freight-backroom-now">
                {freight.backroom_now}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                pallet{freight.backroom_now === 1 ? "" : "s"} left to clear
              </p>
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => setFreightCount((c) => Math.max(1, c - 1))}
                disabled={freightCount <= 1}
                className="w-14 h-14 rounded-full bg-slate-100 hover:bg-slate-200 disabled:opacity-40 flex items-center justify-center"
                data-testid="crew-freight-log-minus"
              >
                <Minus weight="bold" className="w-6 h-6 text-slate-700" />
              </button>
              <div className="min-w-[100px] text-center">
                <p className="text-6xl font-black text-orange-600 leading-none" data-testid="crew-freight-log-value">
                  {freightCount}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mt-1">
                  pallet{freightCount === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFreightCount((c) => Math.min(Math.max(1, freight.backroom_now || 1), c + 1))}
                disabled={freightCount >= Math.max(1, freight.backroom_now || 1)}
                className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-40 flex items-center justify-center"
                data-testid="crew-freight-log-plus"
              >
                <Plus weight="bold" className="w-6 h-6 text-white" />
              </button>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">
                Note (optional)
              </label>
              <input
                type="text"
                value={freightNote}
                onChange={(e) => setFreightNote(e.target.value)}
                maxLength={140}
                placeholder="e.g. cleared A-wall, 3 to floor"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="crew-freight-log-note"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setFreightSheetOpen(false)}
                className="flex-1 h-11 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-black inline-flex items-center justify-center gap-1"
                data-testid="crew-freight-log-cancel"
              >
                <X weight="bold" className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={submitFreight}
                disabled={freightSubmitting || freightCount < 1 || freight.backroom_now <= 0}
                className="flex-1 h-11 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-black inline-flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid="crew-freight-log-submit"
              >
                <Check weight="bold" className="w-4 h-4" />
                {freightSubmitting ? "Logging…" : `Log ${freightCount}`}
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <CompletePhotoDialog
        task={completePromptTask}
        onCancel={() => setCompletePromptTask(null)}
        onConfirm={async (photoUrls) => {
          // `photoUrls` is an array of compressed data-URL strings.
          // IMPORTANT (bug fix 2026-06-21): do NOT close the dialog before
          // the upload finishes. Closing early made the sheet snap back to
          // the Home look with zero proof the photos saved (and hid failures
          // behind a late toast). We now keep the dialog open with an
          // "Uploading…" state and only close it AFTER the request succeeds;
          // on failure we leave it open so the crew can retry. Returns the
          // success boolean so the dialog can manage its own state.
          const t = completePromptTask;
          if (!t) return true;
          const extra = (photoUrls && photoUrls.length)
            ? { photo_data_urls: photoUrls, photo_data_url: photoUrls[photoUrls.length - 1] }
            : {};
          const ok = await transition(t, "complete", extra);
          if (ok !== false) setCompletePromptTask(null);
          return ok;
        }}
      />

      <Dialog open={!!handoffPromptTask} onOpenChange={(o) => !o && setHandoffPromptTask(null)}>
        <DialogContent className="sm:max-w-md" data-testid="handoff-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-black">Request Handoff</DialogTitle>
            <DialogDescription className="text-xs">
              Flags this task as Needs Handoff and pings every manager so they can reassign it before your shift ends.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm font-bold text-slate-900">
            {handoffPromptTask ? categorizeTaskLabel(handoffPromptTask.label).full : ""}
          </p>
          <textarea
            value={handoffNote}
            onChange={(e) => setHandoffNote(e.target.value)}
            placeholder="Optional — why can't you finish? (e.g. customer rush, shift ending in 10)"
            rows={3}
            className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:border-slate-400 outline-none"
            data-testid="handoff-note-input"
          />
          <DialogFooter className="flex-row justify-end gap-2">
            <button type="button" onClick={() => setHandoffPromptTask(null)} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">
              Never mind
            </button>
            <button
              type="button"
              onClick={async () => {
                const t = handoffPromptTask;
                setHandoffPromptTask(null);
                if (t) await transition(t, "request_handoff", handoffNote ? { note: handoffNote } : {});
              }}
              className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-black"
              data-testid="handoff-submit"
            >
              Request Handoff
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch C — Peer "Ask for Help" dialog (2026-06-15). Distinct
          from the Handoff flow above: this bells on-shift teammates
          (NOT managers) so a co-worker can jump in alongside the
          original owner. */}
      <Dialog open={!!helpPromptTask} onOpenChange={(o) => !o && setHelpPromptTask(null)}>
        <DialogContent className="sm:max-w-md" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-black">Ask for Help</DialogTitle>
            <DialogDescription className="text-xs">
              Pings every teammate currently on-shift so someone can jump in. Your manager isn&apos;t notified — this is a peer ask.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm font-bold text-slate-900">
            {helpPromptTask ? categorizeTaskLabel(helpPromptTask.label).full : ""}
          </p>
          <textarea
            value={helpNote}
            onChange={(e) => setHelpNote(e.target.value)}
            placeholder="Optional — what kind of help do you need? (e.g. an extra pair of hands for 10 min)"
            rows={3}
            className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:border-slate-400 outline-none"
            data-testid="help-note-input"
          />
          <DialogFooter className="flex-row justify-end gap-2">
            <button type="button" onClick={() => setHelpPromptTask(null)} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">
              Never mind
            </button>
            <button
              type="button"
              onClick={async () => {
                const t = helpPromptTask;
                setHelpPromptTask(null);
                if (t) await transition(t, "request_help", helpNote ? { note: helpNote } : {});
              }}
              className="px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-black"
              data-testid="help-submit"
            >
              Send Request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Compact single-row summary card — the home-screen footprint. */
function CompactCard({ summary, onView }) {
  const tone = summary.flagged > 0
    ? "bg-rose-50 border-rose-200"
    : summary.total > 0 && summary.done === summary.total
      ? "bg-emerald-50 border-emerald-200"
      : summary.notSeen > 0
        ? "bg-amber-50 border-amber-200"
        : "bg-slate-50 border-slate-200";

  const iconBg = summary.flagged > 0 ? "bg-rose-100" :
                 summary.total > 0 && summary.done === summary.total ? "bg-emerald-100" :
                 summary.notSeen > 0 ? "bg-amber-100" : "bg-slate-100";

  const Icon = summary.flagged > 0 ? Warning :
               summary.total > 0 && summary.done === summary.total ? CheckCircle :
               ClipboardText;
  const iconColor = summary.flagged > 0 ? "text-rose-600" :
                    summary.total > 0 && summary.done === summary.total ? "text-emerald-600" :
                    summary.notSeen > 0 ? "text-amber-600" : "text-slate-400";

  return (
    <div
      className={`w-full rounded-2xl border shadow-sm mb-3 px-4 py-3 flex items-center gap-3 ${tone}`}
      data-testid="your-tasks-today-card"
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon weight={summary.done === summary.total && summary.total > 0 ? "fill" : "duotone"} className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black text-slate-900">
          Your Tasks Today
        </p>
        <p className="text-[11px] text-slate-600 truncate">
          {summary.total === 0
            ? (summary.hasFreight ? "Freight assigned — tap to log progress" : "No assignments yet")
            : (
              <>
                <span className="font-bold">{summary.done} of {summary.total}</span> completed
                {summary.notSeen > 0 && (
                  <> · <span className="font-bold text-amber-700">{summary.notSeen}</span> need acknowledgment</>
                )}
                {summary.flagged > 0 && (
                  <> · <span className="font-bold text-rose-700">{summary.flagged}</span> need attention</>
                )}
                {summary.hasFreight && (
                  <> · <span className="font-bold text-orange-700">Freight</span></>
                )}
              </>
            )}
        </p>
      </div>
      {(summary.notSeen > 0 || summary.flagged > 0) && (
        <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" data-testid="your-tasks-pending-dot" />
      )}
      <button
        type="button"
        onClick={onView}
        className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-black inline-flex items-center gap-1 hover:bg-slate-800 flex-shrink-0"
        data-testid="your-tasks-view-btn"
      >
        View Tasks
        <ArrowRight weight="bold" className="w-3 h-3" />
      </button>
    </div>
  );
}

// FreightAssignmentRow — renders a crew's active freight assignment
// inline in the tasks list. Styled distinctly (orange freight vibe) so
// it doesn't visually blur into WTD/Zone rows, but sits in the same
// menu so the crew has one place to see everything they own today.
function FreightAssignmentRow({ assignment, backroomNow, onLog }) {
  const cleared = Number(assignment?.cleared_pallets || 0);
  const target = assignment?.target_pallets;
  const isTargeted = target != null;
  const pct = isTargeted ? Math.min(100, Math.round((cleared / Number(target)) * 100)) : null;
  const empty = backroomNow <= 0;
  return (
    <div
      className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-3"
      data-testid="crew-freight-assignment-row"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
          <Package weight="fill" className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-orange-700">Freight</p>
          <p className="text-sm font-black text-slate-900 leading-tight">
            {backroomNow} pallet{backroomNow === 1 ? "" : "s"} in the backroom
          </p>
          <p className="text-[11px] text-slate-600 mt-0.5">
            {isTargeted
              ? `You've cleared ${cleared} of ${target}${pct != null ? ` · ${pct}%` : ""}`
              : `You've cleared ${cleared} so far`}
          </p>
        </div>
        <button
          type="button"
          onClick={onLog}
          disabled={empty}
          className="px-3 h-9 rounded-lg bg-orange-600 text-white text-xs font-black inline-flex items-center gap-1 hover:bg-orange-700 disabled:opacity-50 flex-shrink-0"
          data-testid="crew-freight-log-open-btn"
        >
          {empty ? "Empty" : (
            <>
              <Plus weight="bold" className="w-3.5 h-3.5" />
              Log
            </>
          )}
        </button>
      </div>
      {isTargeted && pct != null && (
        <div className="mt-2 h-1.5 rounded-full bg-orange-200 overflow-hidden">
          <div className="h-full bg-orange-600 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}


// Short, store-specific definitions for the 4 world-recovery zone tasks
// (user ask 2026-06-09 — so crew know exactly what each one means).
const ZONE_TASK_HELP = {
  PURGE: "Empty the back room — pull all product to the floor",
  RECOVER: "Pull everything forward — full, faced & shoppable",
  PRICE: "Everything hanging & every shelf priced + labeled correctly",
  ADJUST: "Zebra-scan to true up on-hand counts (e.g. 4 candy bars = 4 showing)",
};

function TaskRow({ task, busy, onAck, onStart, onComplete, onWtdPhoto, onRequestHandoff, onAskHelp, onCancelHelp, onRecall, onOpen }) {
  const meta = STATUS_META[task.status] || STATUS_META.not_acknowledged;
  const cat = categorizeTask({ kind: task.kind, label: task.label });
  const isOwnerActionable = task.kind === "coverage";
  // 2026-02 — zone tasks (PURGE/RECOVER/PRICE/ADJUST) now get an
  // in-sheet action row too. Simpler set than coverage (no ack step,
  // no need-help/handoff, no photo dialog) — just Start → Complete →
  // Waiting-for-verify. User ask: "quick and easy".
  const isZoneActionable = task.kind === "zone";
  // WTD delegations (2026-08-30) — surface the before/after camera flow
  // when the row is `photo_required`. Otherwise fall back to a plain
  // Mark Complete button (assignee's own sign-off).
  const isWtdActionable = task.kind === "win_the_day";
  const isWtdPhotoTask = isWtdActionable && !!task.photo_required;
  const wtdHasBefore = !!task.before_photo_url;
  const wtdHasAfter = !!task.after_photo_url;
  const wtdReady = !isWtdPhotoTask || (wtdHasBefore && wtdHasAfter);
  const wtdPending = task.verification_status === "pending_verification";
  const wtdNeedsCheck = task.verification_status === "needs_manager_check";
  // 2026-08-30 — send-back state: manager rejected the sign-off and
  // asked for a redo. Backend flag is `verification_status="sent_back"`
  // with the note in `sent_back_reason`. Fallback covers rows that
  // pre-date the send-back field split. (Iter 71 finding: crew card
  // rendered like a fresh NOT SEEN row after a send-back — this fixes
  // that gap by rendering a distinct rose Redo banner instead.)
  const wtdSentBack = task.verification_status === "sent_back"
    || (task.status === "not_acknowledged" && !!task.sent_back_reason);
  const wtdSentBackReason = task.sent_back_reason || task.manager_verified_note || "";
  const isVerified = task.status === "manager_verified" || task.verification_status === "verified";
  const isCompleted = task.status === "completed";
  const isHandoff = task.status === "needs_handoff";
  // Batch C — peer help state surfaced on the task row.
  const helpOpen = !!task.help_requested && !task.help_accepted_by;
  const helpAccepted = !!task.help_accepted_by;

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col gap-2"
      data-testid={`task-row-${task.task_id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-black">
            {cat.prefix}
          </p>
          <p className="text-sm font-bold text-slate-900 break-words">{cat.display}</p>
          {/* Win the Day category pill (2026-06-09) — shows which of the
              3 printed-sheet groups this row belongs to (Front End /
              Services · Store Setup · Filling). */}
          {task.kind === "win_the_day" && task.category_label ? (
            <span
              className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-wide"
              data-testid={`wtd-category-${task.task_id}`}
            >
              {task.category_label}
            </span>
          ) : null}
          {/* Zone task (PURGE/RECOVER/PRICE/ADJUST) — show today's Recovery
              World pill + a one-line definition so crew know exactly which
              world to focus on and what the task means. (User ask 2026-06-09.) */}
          {task.kind === "zone" && (
            <>
              {task.world_title ? (
                <span
                  className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide text-white"
                  style={{ backgroundColor: task.world_color || "#0066FF" }}
                  data-testid={`zone-task-world-${task.task_id}`}
                >
                  {task.world_title}
                </span>
              ) : null}
              {ZONE_TASK_HELP[(task.label || "").toUpperCase()] ? (
                <p className="mt-1 text-[11px] text-slate-500 leading-snug" data-testid={`zone-task-help-${task.task_id}`}>
                  {ZONE_TASK_HELP[(task.label || "").toUpperCase()]}
                </p>
              ) : null}
            </>
          )}
          {task.status === "needs_attention" && task.note ? (
            <div className="mt-1 px-2 py-1 bg-rose-50 border border-rose-200 rounded text-[11px] text-rose-700">
              <strong className="font-black">Manager note:</strong> {task.note}
            </div>
          ) : null}
          {isHandoff && task.note ? (
            <div className="mt-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-700">
              <strong className="font-black">Handoff note:</strong> {task.note}
            </div>
          ) : null}
          {task.has_photo && task.completion_photo ? (
            <img
              src={task.completion_photo}
              alt="Completion proof"
              className="mt-2 w-24 h-24 object-cover rounded-lg border border-slate-200"
              data-testid={`task-photo-${task.task_id}`}
            />
          ) : null}
        </div>
        <span
          className={`text-[10px] uppercase tracking-wider font-black px-2 py-0.5 rounded-full border whitespace-nowrap ${
            wtdSentBack
              ? "bg-rose-100 text-rose-800 border-rose-200"
              : meta.chip
          }`}
          data-testid={wtdSentBack ? `task-chip-sent-back-${task.task_id}` : undefined}
        >
          {wtdSentBack ? "Redo" : meta.short}
        </span>
      </div>

      {/* Attribution byline under the row — surfaces "Manager Signed Off
          by Dom" / "Verified by Matthias" so the crew member knows who
          closed it without opening the detail dialog. Empty for
          in-progress / not-acknowledged rows. (User ask 2026-06-13.) */}
      {(() => {
        const byline = taskAttributionLine(task);
        if (!byline || task.status === "completed") return null; // "Awaiting verify" handled by chip
        return (
          <p className="text-[10px] font-bold text-emerald-700" data-testid={`task-attribution-${task.task_id}`}>
            {byline}
          </p>
        );
      })()}

      {/* Owner actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {isOwnerActionable && task.status === "not_acknowledged" && (
          <>
            <button
              type="button"
              onClick={onAck}
              disabled={busy}
              className="flex-1 min-w-[120px] px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-black hover:bg-sky-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
              data-testid={`task-ack-${task.task_id}`}
            >
              <Eye weight="bold" className="w-3.5 h-3.5" />
              Acknowledge
            </button>
            <button
              type="button"
              onClick={onRequestHandoff}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-xs font-black inline-flex items-center justify-center gap-1"
              data-testid={`task-handoff-${task.task_id}`}
            >
              <HandWaving weight="bold" className="w-3.5 h-3.5" />
              Need Help
            </button>
          </>
        )}
        {isOwnerActionable && (task.status === "acknowledged" || task.status === "in_progress") && (
          <>
            {task.status === "acknowledged" && (
              <button
                type="button"
                onClick={onStart}
                disabled={busy}
                className="flex-1 min-w-[110px] px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-black hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
                data-testid={`task-start-${task.task_id}`}
              >
                <PlayCircle weight="bold" className="w-3.5 h-3.5" />
                In Progress
              </button>
            )}
            <button
              type="button"
              onClick={onComplete}
              disabled={busy}
              className="flex-1 min-w-[110px] px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
              data-testid={`task-complete-${task.task_id}`}
            >
              <CheckCircle weight="bold" className="w-3.5 h-3.5" />
              Mark Complete
            </button>
            {!helpOpen && !helpAccepted && (
              <button
                type="button"
                onClick={onAskHelp}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-sky-50 text-sky-700 border border-sky-200 text-xs font-black inline-flex items-center justify-center gap-1"
                data-testid={`task-ask-help-${task.task_id}`}
              >
                <HandWaving weight="bold" className="w-3.5 h-3.5" />
                Ask for Help
              </button>
            )}
            <button
              type="button"
              onClick={onRequestHandoff}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-xs font-black inline-flex items-center justify-center gap-1"
              data-testid={`task-handoff-${task.task_id}`}
            >
              <HandWaving weight="bold" className="w-3.5 h-3.5" />
              Handoff
            </button>
            <button
              type="button"
              onClick={onRecall}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg text-slate-500 text-xs font-bold underline underline-offset-2 disabled:opacity-50 inline-flex items-center justify-center gap-1"
              data-testid={`task-recall-${task.task_id}`}
              title="Didn't mean to acknowledge this? Undo it."
            >
              <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5" />
              Undo
            </button>
          </>
        )}
        {/* Batch C — help state pills shown below the action row so they
            don't compete with the primary actions. (2026-06-15.) */}
        {isOwnerActionable && helpOpen && (
          <div className="w-full flex items-center justify-between gap-2 px-2 py-1 rounded-md bg-sky-50 border border-sky-200">
            <span className="text-[11px] font-bold text-sky-800 inline-flex items-center gap-1">
              <HandWaving weight="bold" className="w-3 h-3" />
              Help requested — waiting for a teammate to jump in…
            </span>
            <button
              type="button"
              onClick={onCancelHelp}
              disabled={busy}
              className="text-[10px] font-black text-sky-700 underline disabled:opacity-50"
              data-testid={`task-cancel-help-${task.task_id}`}
            >
              Cancel
            </button>
          </div>
        )}
        {isOwnerActionable && helpAccepted && (
          <div className="w-full flex items-center justify-between gap-2 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200">
            <span className="text-[11px] font-bold text-emerald-800 inline-flex items-center gap-1">
              <HandWaving weight="bold" className="w-3 h-3" />
              A teammate is helping you on this.
            </span>
            <button
              type="button"
              onClick={onCancelHelp}
              disabled={busy}
              className="text-[10px] font-black text-emerald-700 underline disabled:opacity-50"
              data-testid={`task-clear-help-${task.task_id}`}
            >
              I&apos;ve got it
            </button>
          </div>
        )}
        {isOwnerActionable && task.status === "needs_attention" && (
          <button
            type="button"
            onClick={onComplete}
            disabled={busy}
            className="flex-1 min-w-[120px] px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid={`task-redo-${task.task_id}`}
          >
            <CheckCircle weight="bold" className="w-3.5 h-3.5" />
            I fixed it — Mark Complete
          </button>
        )}
        {isOwnerActionable && isHandoff && (
          <span className="text-[11px] text-amber-700 font-bold italic">
            Waiting for a manager to reassign…
          </span>
        )}
        {isOwnerActionable && isCompleted && (
          <div className="w-full flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-500 italic">
              Waiting for manager to verify…
            </span>
            <button
              type="button"
              onClick={onRecall}
              disabled={busy}
              className="text-[11px] font-black text-rose-600 underline underline-offset-2 disabled:opacity-50 inline-flex items-center gap-1 whitespace-nowrap"
              data-testid={`task-recall-${task.task_id}`}
              title="Marked complete by mistake? Undo it."
            >
              <ArrowCounterClockwise weight="bold" className="w-3 h-3" />
              Oops — not done
            </button>
          </div>
        )}
        {isOwnerActionable && isVerified && (
          <span className="text-[11px] text-emerald-700 font-bold inline-flex items-center gap-1">
            <ShieldCheck weight="fill" className="w-3.5 h-3.5" />
            Manager verified
          </span>
        )}
        {/* Zone tasks — 2026-02 crew self-serve action row. Simplified
            path per user ask "quick and easy": Start button doubles as
            acknowledgment (no separate ack step), then Mark Complete,
            then wait-for-verify. Full assignment / reassignment still
            lives on Zone Sheet — we keep the "Open in Zone Sheet"
            link as a small secondary action below. */}
        {isZoneActionable && task.status === "not_acknowledged" && (
          <button
            type="button"
            onClick={onStart}
            disabled={busy}
            className="flex-1 min-w-[140px] px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-black hover:bg-sky-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid={`zone-task-start-${task.task_id}`}
          >
            <Eye weight="bold" className="w-3.5 h-3.5" />
            Start Task
          </button>
        )}
        {isZoneActionable && task.status === "in_progress" && (
          <button
            type="button"
            onClick={onComplete}
            disabled={busy}
            className="flex-1 min-w-[140px] px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid={`zone-task-complete-${task.task_id}`}
          >
            <CheckCircle weight="bold" className="w-3.5 h-3.5" />
            Mark Complete
          </button>
        )}
        {isZoneActionable && isCompleted && (
          <span className="text-[11px] text-slate-500 italic" data-testid={`zone-task-await-${task.task_id}`}>
            Waiting for manager to verify…
          </span>
        )}
        {isZoneActionable && isVerified && (
          <span className="text-[11px] text-emerald-700 font-bold inline-flex items-center gap-1" data-testid={`zone-task-verified-${task.task_id}`}>
            <ShieldCheck weight="fill" className="w-3.5 h-3.5" />
            Manager verified
          </span>
        )}
        {/* --- WTD delegation actions (2026-08-30) --- */}
        {isWtdActionable && !isVerified && (
          <div className="w-full space-y-2">
            {wtdSentBack && (
              <div className="px-2 py-1.5 bg-rose-50 border border-rose-200 rounded-md text-[11px] text-rose-700" data-testid={`wtd-sent-back-${task.task_id}`}>
                <strong className="font-black">Redo:</strong> {wtdSentBackReason || "Manager asked for a redo — try again."}
              </div>
            )}
            {isWtdPhotoTask && (
              <div className="flex items-center gap-2">
                <WtdPhotoSlot
                  label="Before"
                  photoUrl={task.before_photo_url}
                  onPick={(url) => onWtdPhoto && onWtdPhoto("before", url)}
                  disabled={busy || wtdPending}
                  testid={`wtd-before-${task.task_id}`}
                />
                <WtdPhotoSlot
                  label="After"
                  photoUrl={task.after_photo_url}
                  onPick={(url) => onWtdPhoto && onWtdPhoto("after", url)}
                  disabled={busy || wtdPending}
                  testid={`wtd-after-${task.task_id}`}
                />
              </div>
            )}
            {wtdPending || wtdNeedsCheck ? (
              <div className={`px-2 py-1.5 rounded-md text-[11px] font-bold inline-flex items-center gap-1.5 ${
                wtdPending
                  ? "bg-sky-50 border border-sky-200 text-sky-800"
                  : "bg-amber-50 border border-amber-200 text-amber-800"
              }`}>
                <ShieldCheck weight="duotone" className="w-3.5 h-3.5" />
                {wtdPending ? "Photos in — waiting for manager approval" : "Waiting for manager to spot-check"}
              </div>
            ) : (
              <button
                type="button"
                onClick={onComplete}
                disabled={busy || !wtdReady}
                className="w-full px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
                data-testid={`wtd-complete-${task.task_id}`}
              >
                <CheckCircle weight="bold" className="w-3.5 h-3.5" />
                {isWtdPhotoTask && !wtdReady ? "Attach Before + After to submit" : "Mark Complete"}
              </button>
            )}
          </div>
        )}
        {isWtdActionable && isVerified && (
          <span className="text-[11px] text-emerald-700 font-bold inline-flex items-center gap-1" data-testid={`wtd-verified-${task.task_id}`}>
            <ShieldCheck weight="fill" className="w-3.5 h-3.5" />
            Manager verified
          </span>
        )}
        {!isOwnerActionable && !isZoneActionable && !isWtdActionable && !isCompleted && !isVerified && (
          <button
            type="button"
            onClick={onOpen}
            className="text-[11px] text-sky-700 font-bold underline"
            data-testid={`task-open-${task.task_id}`}
          >
            Open in Zone Sheet →
          </button>
        )}
      </div>
    </div>
  );
}

/** Photo-attach dialog shown when crew taps Mark Complete. Optional —
 *  Skip Photo proceeds without attaching. Supports multi-photo (up to
 *  6 per task, ~1MB each compressed) for jobs that span multiple
 *  sections like a makeup wall. The OS picker (no `capture` attr) lets
 *  the device offer Take Photo / Choose from Library natively, so crew
 *  can attach existing photos instead of forcing a fresh camera capture.
 *  (User asks 2026-06-13: gallery option + multi-photo + add-another.)
 */
function CompletePhotoDialog({ task, onCancel, onConfirm }) {
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]); // array of compressed data URLs
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false); // upload in flight
  const [submitError, setSubmitError] = useState("");

  // Reset photo list every time the dialog opens for a different task.
  useEffect(() => { setPhotos([]); setSubmitting(false); setSubmitError(""); }, [task?.task_id]);

  if (!task) return null;
  const cat = categorizeTask({ kind: task.kind, label: task.label });

  const MAX_PHOTOS = 6;
  const reachedMax = photos.length >= MAX_PHOTOS;

  // Await the parent's save. Keep the dialog open (with an uploading
  // state) until it resolves; the parent closes it on success. On
  // failure we surface an inline error and let the crew retry.
  const doSubmit = async (urls) => {
    if (submitting) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      const ok = await onConfirm(urls);
      if (ok === false) {
        setSubmitError("Upload didn't go through — check your signal and try again.");
      }
    } catch {
      setSubmitError("Upload didn't go through — check your signal and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Single-file picker, but allows the OS to offer both camera and
  // gallery sources. Triggered once per "Add Another Photo" tap so
  // crew can take + upload + take + upload in any combination.
  const handlePick = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset value so picking the same file again still fires onChange.
    if (e.target) e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxDim: 1280, quality: 0.78 });
      setPhotos((prev) => prev.concat([dataUrl]).slice(0, MAX_PHOTOS));
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't read that photo — try again or skip");
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

  return (
    <Dialog open={!!task} onOpenChange={(o) => { if (!o && !submitting) onCancel(); }}>
      <DialogContent className="sm:max-w-md" data-testid="complete-photo-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-black">Attach Completion Photos?</DialogTitle>
          <DialogDescription className="text-xs">
            Optional — timestamped photos help your manager verify the work later. Add as many as you need (up to {MAX_PHOTOS}) for tasks that span multiple sections.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm font-bold text-slate-900">{cat.full}</p>

        {/* Thumbnail strip of already-attached photos */}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid="complete-photo-thumbs">
            {photos.map((url, idx) => (
              <div key={idx} className="relative">
                <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border border-slate-200" />
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 shadow inline-flex items-center justify-center"
                  data-testid={`complete-photo-remove-${idx}`}
                  aria-label="Remove photo"
                >
                  <X weight="bold" className="w-3.5 h-3.5 text-slate-700" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add-photo CTA. Single button that the OS turns into a "Take
            Photo / Choose from Library" sheet on mobile (because we
            removed the `capture` attribute). After at least one photo
            is attached this becomes the "Add Another Photo" button. */}
        {!reachedMax && (
          <button
            type="button"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={busy}
            className="w-full py-5 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 text-xs font-bold inline-flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-50"
            data-testid={photos.length === 0 ? "complete-photo-pick" : "complete-photo-add-another"}
          >
            {photos.length === 0 ? <Camera weight="duotone" className="w-5 h-5" /> : <Plus weight="bold" className="w-4 h-4" />}
            {busy ? "Compressing…" : photos.length === 0 ? "Take or Choose Photo" : `Add Another Photo (${photos.length}/${MAX_PHOTOS})`}
          </button>
        )}
        {reachedMax && (
          <p className="text-[11px] text-slate-500 text-center">Max {MAX_PHOTOS} photos per task — remove one to add a different shot.</p>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handlePick}
          className="hidden"
          data-testid="complete-photo-input"
        />

        {submitError && (
          <p className="text-[12px] font-bold text-rose-600 text-center" data-testid="complete-photo-error">
            {submitError}
          </p>
        )}

        <DialogFooter className="flex-row justify-end gap-2">
          <button
            type="button"
            onClick={() => doSubmit([])}
            disabled={submitting || busy}
            className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold disabled:opacity-50"
            data-testid="complete-photo-skip"
          >
            Skip Photo
          </button>
          <button
            type="button"
            onClick={() => doSubmit(photos)}
            disabled={busy || submitting}
            className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="complete-photo-confirm"
          >
            {submitting && <CircleNotch weight="bold" className="w-3.5 h-3.5 animate-spin" />}
            {submitting
              ? (photos.length > 0 ? `Uploading ${photos.length} photo${photos.length === 1 ? "" : "s"}…` : "Submitting…")
              : (photos.length > 0
                  ? `Submit with ${photos.length} Photo${photos.length === 1 ? "" : "s"}`
                  : "Submit without Photo")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * WtdPhotoSlot — small camera tile used by WTD photo-required rows to
 * capture the "before" and "after" shots. Tap to open the OS picker
 * (camera OR gallery on mobile), compresses the image, and hands the
 * data URL to the parent via `onPick`. When a photo is already
 * attached, renders the thumbnail with a re-take hover — but the
 * server side blocks re-uploads once the row is in
 * `pending_verification`, so we also disable via the `disabled` prop.
 */
function WtdPhotoSlot({ label, photoUrl, onPick, disabled, testid }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxDim: 1280, quality: 0.78 });
      onPick && onPick(dataUrl);
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't read that photo — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1">
      <button
        type="button"
        onClick={() => !disabled && fileRef.current && fileRef.current.click()}
        disabled={disabled || busy}
        className={`w-full h-24 rounded-xl border-2 border-dashed inline-flex flex-col items-center justify-center gap-1 relative overflow-hidden ${
          photoUrl
            ? "border-slate-200 bg-slate-50"
            : "border-slate-300 bg-white hover:bg-slate-50 text-slate-500"
        } ${(disabled || busy) ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testid}
      >
        {photoUrl ? (
          <>
            <img src={photoUrl} alt={label} className="absolute inset-0 w-full h-full object-cover" />
            <span className="relative z-10 self-start ml-1 mt-1 text-[9px] font-black uppercase bg-slate-900/80 text-white px-1 rounded">
              {label}
            </span>
            {!disabled && (
              <span className="relative z-10 mt-auto mb-1 text-[10px] font-black text-white bg-slate-900/70 px-1.5 py-0.5 rounded">
                Re-take
              </span>
            )}
          </>
        ) : (
          <>
            <Camera weight="duotone" className="w-6 h-6" />
            <span className="text-[10px] font-black uppercase tracking-wider">{busy ? "…" : label}</span>
          </>
        )}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handlePick}
        className="hidden"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
